import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChartLine } from "@phosphor-icons/react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/shared";
import { toast } from "sonner";

const COLORS = ["hsl(174 62% 34%)", "hsl(266 58% 48%)", "hsl(28 88% 48%)",
  "hsl(210 72% 45%)", "hsl(340 62% 48%)", "hsl(150 52% 36%)", "hsl(12 76% 52%)",
  "hsl(255 40% 40%)", "hsl(196 68% 40%)"];

const monthLabel = (p) => {
  const [y, m] = p.split("-");
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+m - 1]} ${y.slice(2)}`;
};

export default function ScoreTrends() {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("category");
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/dinacharya/scores/history`)
      .then(({ data: d }) => {
        setData(d);
        setPicked(d.series.filter((s) => s.kind === "category").map((s) => s.key).slice(0, 3));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [tenantId]);

  const options = useMemo(
    () => (data?.series || []).filter((s) => s.kind === mode), [data, mode]);
  const chart = useMemo(
    () => (data?.chart || []).map((row) => ({ ...row, label: monthLabel(row.period) })), [data]);

  const toggle = (key) =>
    setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  return (
    <div className="space-y-8" data-testid="dc-score-trends">
      <Link to="/dinacharya" data-testid="trends-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-navy hover:text-teal">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-white"><ArrowLeft size={13} /></span>
        Back to Dinacharya
      </Link>

      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex items-start gap-5">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-teal text-white">
              <ChartLine size={26} />
            </div>
            <div>
              <h1 className="font-display text-3xl tracking-tight text-navy">Score trends</h1>
              <p className="mt-1 text-sm text-foreground/75">
                Month-on-month scores captured from the score categorisation sheet — by team category
                and by individual member.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {[["category", "By category"], ["member", "By member"]].map(([k, label]) => (
              <button key={k}
                      onClick={() => {
                        setMode(k);
                        setPicked((data?.series || []).filter((s) => s.kind === k).map((s) => s.key).slice(0, 3));
                      }}
                      data-testid={`trend-mode-${k}`}
                      className={`rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                        mode === k ? "bg-teal text-white" : "border border-border text-navy hover:border-teal/50"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!data?.periods?.length ? (
        <EmptyState testid="trends-empty" title="No history captured yet"
                    message="Scores are stored once a month from the client's sheet. Once two months of readings exist, the trend line appears here." />
      ) : (
        <>
          <div className="panel p-6" data-testid="trend-picker">
            <p className="label-caps">Pick the lines to compare</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {options.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No {mode === "member" ? "member-level" : "category"} readings captured yet.
                </p>
              )}
              {options.map((s, i) => (
                <button key={s.key} onClick={() => toggle(s.key)} data-testid={`trend-pick-${s.key}`}
                        className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                          picked.includes(s.key)
                            ? "border-transparent text-white"
                            : "border-border text-navy hover:border-teal/50"}`}
                        style={picked.includes(s.key) ? { backgroundColor: COLORS[i % COLORS.length] } : undefined}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="panel p-6" data-testid="trend-chart">
            <div className="h-[26rem]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {picked.map((key) => {
                    const s = data.series.find((x) => x.key === key);
                    const idx = options.findIndex((x) => x.key === key);
                    return (
                      <Line key={key} type="monotone" dataKey={key} name={s?.label || key}
                            stroke={COLORS[(idx < 0 ? 0 : idx) % COLORS.length]} strokeWidth={2}
                            dot={{ r: 3 }} connectNulls />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="panel overflow-x-auto" data-testid="trend-table">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    {mode === "member" ? "Member" : "Category"}
                  </th>
                  {data.periods.map((p) => (
                    <th key={p} className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {monthLabel(p)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {options.map((s) => (
                  <tr key={s.key} data-testid={`trend-row-${s.key}`}>
                    <td className="px-5 py-3 font-medium text-navy">{s.label}</td>
                    {data.periods.map((p) => {
                      const row = data.chart.find((c) => c.period === p);
                      const v = row?.[s.key];
                      return (
                        <td key={p} className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                          {v == null ? "—" : v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
